import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { Player } from "@/lib/types";

interface MissingVariableIssue {
  playerName: string;
  playerId: number;
  missingVariables: { variable: string; label: string; }[];
}

interface MissingVariableWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: (selectedPlayerIds?: number[]) => void;
  issues: MissingVariableIssue[];
  players: Player[];
}

export default function MissingVariableWarningModal({
  isOpen,
  onClose,
  onContinue,
  issues,
  players,
}: MissingVariableWarningModalProps) {
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<Set<number>>(
    new Set(issues.map(issue => issue.playerId))
  );

  const handlePlayerSelection = (playerId: number, checked: boolean) => {
    const newSelected = new Set(selectedPlayerIds);
    if (checked) {
      newSelected.add(playerId);
    } else {
      newSelected.delete(playerId);
    }
    setSelectedPlayerIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPlayerIds(new Set(issues.map(issue => issue.playerId)));
    } else {
      setSelectedPlayerIds(new Set());
    }
  };

  const handleProceed = () => {
    onContinue(Array.from(selectedPlayerIds));
  };

  const handleContinueAll = () => {
    onContinue(); // Continue with all players (no filtering)
  };

  // Get player details for each issue
  const getPlayerDetails = (playerId: number) => {
    return players.find(p => p.id === playerId);
  };

  const allSelected = selectedPlayerIds.size === issues.length;
  const noneSelected = selectedPlayerIds.size === 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" style={{ zIndex: 70 }}>
        <DialogHeader>
          <DialogTitle className="flex items-center text-lg">
            <i className="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
            Missing Variable Data
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Warning Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <i className="fas fa-exclamation-triangle text-yellow-400"></i>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Missing Player Data for Email Variables
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Some players are missing data for variables used in your email template. 
                    You can choose to proceed with selected players or exclude those with missing data.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Player Selection Controls */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleSelectAll}
                className="rounded border-gray-300"
              />
              <span className="text-sm font-medium text-gray-700">
                Select All Players ({issues.length})
              </span>
            </div>
            <div className="text-sm text-gray-600">
              {selectedPlayerIds.size} of {issues.length} selected
            </div>
          </div>

          {/* Players List */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">
                Players with Missing Data ({issues.length})
              </h4>
            </div>
            
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {issues.map((issue) => {
                const player = getPlayerDetails(issue.playerId);
                const isSelected = selectedPlayerIds.has(issue.playerId);
                
                return (
                  <div key={issue.playerId} className={`p-4 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
                    <div className="flex items-start space-x-3">
                      {/* Checkbox */}
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handlePlayerSelection(issue.playerId, checked as boolean)}
                        className="mt-1 rounded border-gray-300"
                      />
                      
                      {/* Avatar */}
                      <AvatarPlaceholder 
                        name={issue.playerName} 
                        className="w-10 h-10 flex-shrink-0"
                      />
                      
                      {/* Player Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="text-sm font-medium text-gray-900 truncate">
                              {issue.playerName}
                            </h5>
                            
                            {/* Player Details */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs mt-1">
                              {player?.current_club && (
                                <div>
                                  <span className="font-medium text-gray-500">Club:</span> 
                                  <span className="ml-1 text-gray-700">{player.current_club}</span>
                                </div>
                              )}
                              {player?.position && player.position.length > 0 && (
                                <div>
                                  <span className="font-medium text-gray-500">Position:</span> 
                                  <span className="ml-1 text-gray-700">{player.position.join(', ')}</span>
                                </div>
                              )}
                              {player?.email && (
                                <div>
                                  <span className="font-medium text-gray-500">Email:</span> 
                                  <span className="ml-1 text-gray-700">{player.email}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Missing Variables */}
                            <div className="mt-2">
                              <p className="text-xs text-gray-600 mb-1">Missing data for:</p>
                              <div className="flex flex-wrap gap-1">
                                {issue.missingVariables.map((missingVar, varIndex) => (
                                  <span
                                    key={varIndex}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                                  >
                                    {missingVar.label}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">What happens next?</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Send to Selected Players:</strong> Only send emails to players you've selected above</li>
                  <li><strong>Continue with All:</strong> Send to all players, variable placeholders will remain as-is for those with missing data</li>
                  <li><strong>Cancel:</strong> Go back to edit your email or add missing player data</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleProceed}
              disabled={noneSelected}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Send to Selected Players ({selectedPlayerIds.size})
            </Button>
            <Button
              onClick={handleContinueAll}
              variant="outline"
              className="bg-yellow-600 hover:bg-yellow-700 text-white border-yellow-600"
            >
              Continue with All
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 